import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {useDispatch, useSelector} from 'react-redux';
import {useLocation} from 'react-router-dom';
import cn from 'classnames';
import IconSidebarHome from 'assets/IconSidebarHome';
import IconSidebarAnalytics from 'assets/IconSidebarAnalytics';
import IconSidebarTeam from 'assets/IconSidebarTeam';
import IconSidebarOrders from 'assets/IconSidebarOrders';
import {IconSidebarBookADriver} from 'assets/IconSidebarBookADriver';
import IconSidebarInsights from 'assets/IconSidebarInsights';
import IconSidebarMenuEditor from 'assets/IconSidebarMenuEditor';
import IconSidebarReports from 'assets/IconSidebarReports';
import IconSidebarSettings from 'assets/IconSidebarSettings';
import IconSidebarHelp from 'assets/IconSidebarHelp';
import {
  Logo,
  SidebarArrow,
  SidebarContainer,
  SidebarHolder,
  SidebarLocation,
  SidebarLocationLabel,
  SidebarLocationSelectWrapper,
  SidebarLocationWrapper,
  SidebarMenu,
  SidebarOverlay,
  SidebarProfile,
  SidebarProfileInfo,
  SidebarProfileLogoutButton,
  SidebarProfileName,
  SidebarProfilePicture,
  SidebarProfilePosition,
  SidebarWrapper
} from 'components/layout/sidebar/styles';
import {
  GET_LOCATION,
  GET_LOCATION_ID,
  GET_TIMEZONE
} from 'store/global/user/action';
import {LOGOUT_USER} from 'store/global/auth/action';
import RestrictedArea from 'components/layout/RestrictedArea';
import {
  FEATURE_RESTRICTION_IDS,
  getBookADriverRestriction,
  getInsightsRestriction,
  getOrderV2Restriction,
  checkIfSauceEmployee,
  hasBookADriverAccess
} from 'helpers/constants/restrictions';
import SidebarMenuItem from 'components/layout/sidebar/SidebarMenuItem';
import IconSidebarArrow from 'assets/icon-sidebar-arrow.svg';
import SauceLogo from 'assets/logo-sauce-white.svg';
import stringToBoolean from 'helpers/stringToBoolean';
import SelectComponent from 'components/select/SelectComponent';
import usePostHogFeatureFlag from 'hooks/usePostHogFeatureFlag';
import SidebarVariantContext, {
  SIDEBAR_NAV_FLAG
} from 'components/layout/sidebar/SidebarVariantContext';
import SidebarSuccessManager from 'components/SuccessManager/SidebarSuccessManager';

const Icon = ({children}) => children;
Icon.propTypes = {children: PropTypes.element.isRequired};

const SidebarContent = ({showSidebar, setShowSidebar, variant}) => {
  const isNavV2 = variant === 'test';
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const location = useLocation();

  const locations = useSelector((state) => state.user.locations);
  const locationId = useSelector((state) => state.user.locationId);
  const activeLocationId = useSelector(
    (state) => state.user?.activeLocation?._id
  );
  const isByocOnly = useSelector(
    (state) =>
      state.user.activeLocation?.IsByocOnly === true ||
      state.user.activeLocation?.isByocOnly === true
  );

  const serviceFeatures = useSelector(
    (state) => state.user?.activeLocation?.AllowedServiceFeatures
  );

  const isComoLocation = useSelector(
    (state) => state.user?.activeLocation?.IsComoLocation
  );

  const closeSidebarEvent = () => {
    setShowSidebar(!showSidebar);
  };

  const handleLocationChange = (event) => {
    dispatch(
      GET_LOCATION_ID({
        locationId: event.target.value,
        isToast: stringToBoolean(
          event.currentTarget.getAttribute('data-isToast')
        )
      })
    );
    dispatch(GET_TIMEZONE(event.currentTarget.getAttribute('data-timezone')));
    dispatch(
      GET_LOCATION(
        JSON.parse(event.currentTarget.getAttribute('data-fullItem'))
      )
    );
  };

  const email = user?.userData?.email || '';
  const emailName = email.substring(0, email.lastIndexOf('@')) || email;
  const firstName = user?.userData?.first_name;
  const lastName = user?.userData?.last_name;
  const displayName =
    firstName && lastName ? `${firstName} ${lastName}` : emailName;
  const roleRaw = user?.userData?.role || '';
  const role = roleRaw
    ? roleRaw.charAt(0).toUpperCase() + roleRaw.slice(1).toLowerCase()
    : '';
  const avatarInitial =
    firstName && lastName
      ? `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
      : (emailName.charAt(0) || '?').toUpperCase();

  const matchesRoute = (route) =>
    location.pathname === route || location.pathname.startsWith(`${route}/`);

  const isInsightsRoute = ['/insights', '/analytics'].some(matchesRoute);

  const isGrowthRoute = [
    '/marketing',
    '/marketing-v2',
    '/flyers',
    '/discounts',
    '/discounts-v2',
    '/virtual-number',
    '/cashback',
    '/invite-a-bite'
  ].some(matchesRoute);

  const isCustomersRoute = ['/feedback', '/customer-insights'].some(
    matchesRoute
  );

  const isSettingsRoute = [
    '/store-settings',
    '/store-appearance',
    '/team',
    '/marketing-integrations',
    '/my-account',
    ...(isNavV2 ? ['/insights/growth'] : [])
  ].some(matchesRoute);

  const [openGroups, setOpenGroups] = useState({
    insights: isInsightsRoute,
    growth: true,
    customers: isCustomersRoute,
    settings: isSettingsRoute
  });

  useEffect(() => {
    setOpenGroups((prev) => ({
      insights: prev.insights || isInsightsRoute,
      growth: prev.growth || isGrowthRoute,
      customers: prev.customers || isCustomersRoute,
      settings: prev.settings || isSettingsRoute
    }));
  }, [isInsightsRoute, isGrowthRoute, isCustomersRoute, isSettingsRoute]);

  const toggleGroup = (key) =>
    setOpenGroups((prev) => ({...prev, [key]: !prev[key]}));

  const reportsItem = (
    <RestrictedArea
      restrictions={[]}
      features={[FEATURE_RESTRICTION_IDS.REPORTS]}
      extraAccessLogic={() => user.userData.status === 'ACTIVE'}
    >
      <SidebarMenuItem
        icon={
          <Icon>
            <IconSidebarReports />
          </Icon>
        }
        text='Reports'
        route='/reports'
        feature={FEATURE_RESTRICTION_IDS.REPORTS}
        closeSidebarEvent={closeSidebarEvent}
      />
    </RestrictedArea>
  );

  return (
    <SidebarWrapper className={showSidebar ? 'Sidebar isOpen' : 'Sidebar'}>
      <SidebarContainer>
        <Logo>
          <img src={SauceLogo} alt='logo' />
        </Logo>
        {Array.isArray(locations) && locations.length > 0 && (
          <SidebarLocationWrapper>
            <SidebarLocation>
              <SidebarLocationLabel>Location</SidebarLocationLabel>
              <SidebarLocationSelectWrapper>
                <SelectComponent
                  onChange={handleLocationChange}
                  options={locations}
                  value={activeLocationId || locationId || ''}
                  defaultLabel='Choose the location'
                  disableScrollLock
                />
              </SidebarLocationSelectWrapper>
            </SidebarLocation>
          </SidebarLocationWrapper>
        )}
        <SidebarHolder>
          <SidebarMenu>
            <RestrictedArea
              restrictions={[]}
              features={[FEATURE_RESTRICTION_IDS.MAIN_PAGE]}
              extraAccessLogic={() => user.userData.status === 'ACTIVE'}
            >
              <SidebarMenuItem
                icon={
                  <Icon>
                    <IconSidebarHome />
                  </Icon>
                }
                text='Home'
                route='/main-page'
                feature={FEATURE_RESTRICTION_IDS.MAIN_PAGE}
                closeSidebarEvent={closeSidebarEvent}
              />
            </RestrictedArea>
            <RestrictedArea
              restrictions={[]}
              features={[FEATURE_RESTRICTION_IDS.ORDERS]}
              extraAccessLogic={() => user.userData.status === 'ACTIVE'}
            >
              <SidebarMenuItem
                icon={
                  <Icon>
                    <IconSidebarOrders />
                  </Icon>
                }
                text='Orders'
                route='/orders'
                feature={FEATURE_RESTRICTION_IDS.ORDERS}
                closeSidebarEvent={closeSidebarEvent}
              />
            </RestrictedArea>
            <RestrictedArea
              restrictions={[]}
              features={[getOrderV2Restriction(serviceFeatures)]}
              extraAccessLogic={() => user.userData.status === 'ACTIVE'}
            >
              <SidebarMenuItem
                icon={
                  <Icon>
                    <IconSidebarOrders />
                  </Icon>
                }
                text='Orders'
                route='/orders-v2'
                feature={getOrderV2Restriction(serviceFeatures)}
                closeSidebarEvent={closeSidebarEvent}
              />
            </RestrictedArea>

            <RestrictedArea
              restrictions={[]}
              features={
                checkIfSauceEmployee()
                  ? []
                  : [getBookADriverRestriction(serviceFeatures)].filter(Boolean)
              }
              extraAccessLogic={() =>
                user.userData.status === 'ACTIVE' &&
                hasBookADriverAccess(serviceFeatures || [])
              }
            >
              <SidebarMenuItem
                icon={
                  <Icon>
                    <IconSidebarBookADriver />
                  </Icon>
                }
                text='Book a Driver'
                route='/book-a-driver'
                feature={
                  getBookADriverRestriction(serviceFeatures) ||
                  FEATURE_RESTRICTION_IDS.BOOK_A_DRIVER_V2
                }
                closeSidebarEvent={closeSidebarEvent}
              />
            </RestrictedArea>

            {isNavV2 && (
              <RestrictedArea
                restrictions={['analyticsAccess']}
                features={[FEATURE_RESTRICTION_IDS.ANALYTICS]}
                extraAccessLogic={() => user.userData.status === 'ACTIVE'}
              >
                <SidebarMenuItem
                  icon={
                    <Icon>
                      <IconSidebarInsights />
                    </Icon>
                  }
                  text='Analytics'
                  route='/analytics'
                  feature={FEATURE_RESTRICTION_IDS.ANALYTICS}
                  closeSidebarEvent={closeSidebarEvent}
                />
              </RestrictedArea>
            )}
            {isNavV2 && reportsItem}

            <RestrictedArea
              restrictions={[]}
              features={[getInsightsRestriction(serviceFeatures)]}
              extraAccessLogic={() => !isNavV2}
            >
              <SidebarMenuItem
                isGroup
                isActive={isInsightsRoute}
                onClick={() => toggleGroup('insights')}
                feature={getInsightsRestriction(serviceFeatures)}
                text='Insights'
                icon={
                  <Icon>
                    <IconSidebarInsights />
                  </Icon>
                }
                subItems={
                  <SidebarMenu className={cn({isOpen: openGroups.insights})}>
                    {serviceFeatures?.includes(
                      FEATURE_RESTRICTION_IDS.INSIGHTS_GROWTH
                    ) && (
                      <SidebarMenuItem
                        route='/insights/growth'
                        text='Direct Optimization'
                        icon={null}
                        closeSidebarEvent={closeSidebarEvent}
                      />
                    )}
                    {serviceFeatures?.includes(
                      FEATURE_RESTRICTION_IDS.INSIGHTS_PERFORMANCE
                    ) && (
                      <SidebarMenuItem
                        route='/insights/performance'
                        text='Performance'
                        icon={null}
                        closeSidebarEvent={closeSidebarEvent}
                      />
                    )}
                    <RestrictedArea
                      restrictions={['analyticsAccess']}
                      features={[FEATURE_RESTRICTION_IDS.ANALYTICS]}
                      extraAccessLogic={() => user.userData.status === 'ACTIVE'}
                    >
                      <SidebarMenuItem
                        route='/analytics'
                        text='Analytics'
                        icon={null}
                        feature={FEATURE_RESTRICTION_IDS.ANALYTICS}
                        closeSidebarEvent={closeSidebarEvent}
                      />
                    </RestrictedArea>
                  </SidebarMenu>
                }
                arrowIcon={
                  <SidebarArrow
                    src={IconSidebarArrow}
                    alt=''
                    className={cn({isOpen: openGroups.insights})}
                  />
                }
              />
            </RestrictedArea>

            <RestrictedArea
              restrictions={['growth_section']}
              features={[]}
              extraAccessLogic={() => user.userData.status === 'ACTIVE'}
            >
              <SidebarMenuItem
                isGroup
                isActive={isGrowthRoute}
                onClick={() => toggleGroup('growth')}
                text='Growth'
                icon={
                  <Icon>
                    <IconSidebarAnalytics />
                  </Icon>
                }
                subItems={
                  <SidebarMenu className={cn({isOpen: openGroups.growth})}>
                    <RestrictedArea
                      restrictions={[]}
                      features={[FEATURE_RESTRICTION_IDS.CASHBACK]}
                      extraAccessLogic={() =>
                        user.userData.status === 'ACTIVE' && !isComoLocation
                      }
                    >
                      <SidebarMenuItem
                        route='/cashback'
                        text='Loyalty & Cashback'
                        icon={null}
                        closeSidebarEvent={closeSidebarEvent}
                      />
                    </RestrictedArea>
                    <RestrictedArea
                      restrictions={[]}
                      features={[
                        FEATURE_RESTRICTION_IDS.EMAIL_MARKETING,
                        FEATURE_RESTRICTION_IDS.EMAIL_MARKETING_DASHBOARD_V2
                      ]}
                      extraAccessLogic={() =>
                        user.userData.status === 'ACTIVE' && !isComoLocation
                      }
                    >
                      <SidebarMenuItem
                        route='/marketing-v2'
                        text='Email Marketing'
                        icon={null}
                        closeSidebarEvent={closeSidebarEvent}
                      />
                    </RestrictedArea>
                    <RestrictedArea
                      restrictions={[]}
                      features={[FEATURE_RESTRICTION_IDS.EMAIL_MARKETING]}
                      extraAccessLogic={() =>
                        user.userData.status === 'ACTIVE' &&
                        !isComoLocation &&
                        !(serviceFeatures || []).includes(
                          FEATURE_RESTRICTION_IDS.EMAIL_MARKETING_DASHBOARD_V2
                        )
                      }
                    >
                      <SidebarMenuItem
                        route='/marketing'
                        text='Email Marketing'
                        icon={null}
                        closeSidebarEvent={closeSidebarEvent}
                      />
                    </RestrictedArea>
                    <RestrictedArea
                      restrictions={[]}
                      features={[FEATURE_RESTRICTION_IDS.DISCOUNTS_V2]}
                      extraAccessLogic={() =>
                        user.userData.status === 'ACTIVE' && !isComoLocation
                      }
                    >
                      <SidebarMenuItem
                        route='/discounts-v2'
                        text='Discounts'
                        icon={null}
                        closeSidebarEvent={closeSidebarEvent}
                      />
                    </RestrictedArea>
                    <RestrictedArea
                      restrictions={[]}
                      features={[FEATURE_RESTRICTION_IDS.DISCOUNTS]}
                      extraAccessLogic={() =>
                        user.userData.status === 'ACTIVE' && !isComoLocation
                      }
                    >
                      <SidebarMenuItem
                        route='/discounts'
                        text='Discounts'
                        icon={null}
                        closeSidebarEvent={closeSidebarEvent}
                      />
                    </RestrictedArea>
                    <RestrictedArea
                      restrictions={[]}
                      features={[FEATURE_RESTRICTION_IDS.INVITE_A_BITE]}
                    >
                      <SidebarMenuItem
                        route='/invite-a-bite'
                        text='Invite a Bite'
                        icon={null}
                        closeSidebarEvent={closeSidebarEvent}
                      />
                    </RestrictedArea>
                    <RestrictedArea
                      restrictions={[]}
                      features={[FEATURE_RESTRICTION_IDS.FLYERS]}
                      extraAccessLogic={() => user.userData.status === 'ACTIVE'}
                    >
                      <SidebarMenuItem
                        route='/flyers'
                        text='Flyers'
                        icon={null}
                        closeSidebarEvent={closeSidebarEvent}
                      />
                    </RestrictedArea>
                    <RestrictedArea
                      restrictions={['virtualNumberAccess']}
                      features={[FEATURE_RESTRICTION_IDS.VIRTUAL_NUMBERS]}
                      extraAccessLogic={() =>
                        user.userData.status === 'ACTIVE' &&
                        Boolean(user.activeLocation?.GeneratedLink) &&
                        Boolean(user.activeLocation?.GeneratedLinkURL)
                      }
                    >
                      <SidebarMenuItem
                        route='/virtual-number'
                        text='Virtual Answering'
                        icon={null}
                        closeSidebarEvent={closeSidebarEvent}
                      />
                    </RestrictedArea>
                  </SidebarMenu>
                }
                arrowIcon={
                  <SidebarArrow
                    src={IconSidebarArrow}
                    alt=''
                    className={cn({isOpen: openGroups.growth})}
                  />
                }
              />
            </RestrictedArea>

            <RestrictedArea
              restrictions={[]}
              features={[FEATURE_RESTRICTION_IDS.MENU_EDITOR]}
              extraAccessLogic={() => !user.isToast}
            >
              <SidebarMenuItem
                icon={
                  <Icon>
                    <IconSidebarMenuEditor />
                  </Icon>
                }
                text='Menu Editor'
                route='/menu-editor/categories'
                feature={FEATURE_RESTRICTION_IDS.MENU_EDITOR}
                closeSidebarEvent={closeSidebarEvent}
              />
            </RestrictedArea>

            <RestrictedArea
              restrictions={['customers_section']}
              features={[]}
              extraAccessLogic={() => user.userData.status === 'ACTIVE'}
            >
              <SidebarMenuItem
                isGroup
                isActive={isCustomersRoute}
                onClick={() => toggleGroup('customers')}
                text='Customers'
                icon={
                  <Icon>
                    <IconSidebarTeam />
                  </Icon>
                }
                subItems={
                  <SidebarMenu className={cn({isOpen: openGroups.customers})}>
                    <RestrictedArea
                      restrictions={[]}
                      features={[FEATURE_RESTRICTION_IDS.CUSTOMER_INSIGHTS]}
                      extraAccessLogic={() =>
                        user.userData.status === 'ACTIVE' && !isByocOnly
                      }
                    >
                      <SidebarMenuItem
                        route='/customer-insights'
                        text={isNavV2 ? 'Insights' : 'Customer Insights'}
                        icon={null}
                        feature={FEATURE_RESTRICTION_IDS.CUSTOMER_INSIGHTS}
                        closeSidebarEvent={closeSidebarEvent}
                      />
                    </RestrictedArea>
                    <RestrictedArea
                      restrictions={[]}
                      features={[FEATURE_RESTRICTION_IDS.FEEDBACK]}
                      extraAccessLogic={() => user.userData.status === 'ACTIVE'}
                    >
                      <SidebarMenuItem
                        route='/feedback'
                        text={isNavV2 ? 'Reviews' : 'Customer Feedback'}
                        feature={FEATURE_RESTRICTION_IDS.FEEDBACK}
                        extraNewTagDisplayLogic={() => isNavV2}
                        icon={null}
                        closeSidebarEvent={closeSidebarEvent}
                      />
                    </RestrictedArea>
                  </SidebarMenu>
                }
                arrowIcon={
                  <SidebarArrow
                    src={IconSidebarArrow}
                    alt=''
                    className={cn({isOpen: openGroups.customers})}
                  />
                }
              />
            </RestrictedArea>

            {!isNavV2 && reportsItem}

            <RestrictedArea
              restrictions={['settings_section']}
              features={[]}
              extraAccessLogic={() => user.userData.status === 'ACTIVE'}
            >
              <SidebarMenuItem
                isGroup
                isActive={isSettingsRoute}
                onClick={() => toggleGroup('settings')}
                text='Settings'
                icon={
                  <Icon>
                    <IconSidebarSettings />
                  </Icon>
                }
                subItems={
                  <SidebarMenu className={cn({isOpen: openGroups.settings})}>
                    <RestrictedArea
                      restrictions={['locationSettingsEdit']}
                      features={[FEATURE_RESTRICTION_IDS.STORE_SETTINGS]}
                      extraAccessLogic={() => user.userData.status === 'ACTIVE'}
                    >
                      <SidebarMenuItem
                        route='/store-settings'
                        text='Store Settings'
                        icon={null}
                        closeSidebarEvent={closeSidebarEvent}
                      />
                    </RestrictedArea>
                    <RestrictedArea
                      restrictions={['store_appearance']}
                      features={[]}
                      extraAccessLogic={() => user.userData.status === 'ACTIVE'}
                    >
                      <SidebarMenuItem
                        route='/store-appearance'
                        text='Store Appearance'
                        icon={null}
                        closeSidebarEvent={closeSidebarEvent}
                      />
                    </RestrictedArea>
                    <RestrictedArea
                      restrictions={[]}
                      features={[FEATURE_RESTRICTION_IDS.TEAM]}
                      extraAccessLogic={() => user.userData.status !== 'NEW'}
                    >
                      <SidebarMenuItem
                        route='/team'
                        text='Team'
                        icon={null}
                        closeSidebarEvent={closeSidebarEvent}
                      />
                    </RestrictedArea>
                    <RestrictedArea
                      restrictions={[]}
                      features={[
                        FEATURE_RESTRICTION_IDS.MARKETING_INTEGRATIONS
                      ]}
                    >
                      <SidebarMenuItem
                        route='/marketing-integrations'
                        text='Marketing Integrations'
                        icon={null}
                        closeSidebarEvent={closeSidebarEvent}
                      />
                    </RestrictedArea>
                    <RestrictedArea
                      restrictions={[]}
                      features={[FEATURE_RESTRICTION_IDS.MY_ACCOUNT]}
                    >
                      <SidebarMenuItem
                        route='/my-account'
                        text='My Account'
                        icon={null}
                        closeSidebarEvent={closeSidebarEvent}
                      />
                    </RestrictedArea>
                    {isNavV2 && (
                      <RestrictedArea
                        restrictions={[]}
                        features={[FEATURE_RESTRICTION_IDS.INSIGHTS_GROWTH]}
                      >
                        <SidebarMenuItem
                          route='/insights/growth'
                          text='Direct Optimization'
                          icon={null}
                          closeSidebarEvent={closeSidebarEvent}
                        />
                      </RestrictedArea>
                    )}
                  </SidebarMenu>
                }
                arrowIcon={
                  <SidebarArrow
                    src={IconSidebarArrow}
                    alt=''
                    className={cn({isOpen: openGroups.settings})}
                  />
                }
              />
            </RestrictedArea>

            <RestrictedArea
              restrictions={[]}
              features={[FEATURE_RESTRICTION_IDS.HELP]}
            >
              <SidebarMenuItem
                icon={
                  <Icon>
                    <IconSidebarHelp />
                  </Icon>
                }
                text='Help'
                route='/help'
                feature={FEATURE_RESTRICTION_IDS.HELP}
                closeSidebarEvent={closeSidebarEvent}
              />
            </RestrictedArea>
          </SidebarMenu>
        </SidebarHolder>
      </SidebarContainer>
      <SidebarSuccessManager />
      {user.userData && Object.keys(user.userData).length !== 0 && (
        <SidebarProfile>
          <SidebarProfilePicture>{avatarInitial}</SidebarProfilePicture>
          <SidebarProfileInfo>
            <SidebarProfileName>
              <span>{displayName}</span>
            </SidebarProfileName>
            <SidebarProfilePosition>{role}</SidebarProfilePosition>
            <SidebarProfileLogoutButton
              onClick={() => {
                dispatch(LOGOUT_USER());
                if (window?.posthog) {
                  window?.posthog?.reset();
                }
              }}
              href={`${process.env.REACT_APP_OLD_DASHBOARD_URL}/logout`}
            />
          </SidebarProfileInfo>
        </SidebarProfile>
      )}
      <SidebarOverlay
        onClick={() => setShowSidebar(!showSidebar)}
        className='SidebarOverlay'
      />
    </SidebarWrapper>
  );
};

SidebarContent.propTypes = {
  showSidebar: PropTypes.bool.isRequired,
  setShowSidebar: PropTypes.func.isRequired,
  variant: PropTypes.string.isRequired
};

const Sidebar = ({showSidebar, setShowSidebar}) => {
  const variant = usePostHogFeatureFlag(SIDEBAR_NAV_FLAG);

  return (
    <SidebarVariantContext.Provider value={variant}>
      <SidebarContent
        showSidebar={showSidebar}
        setShowSidebar={setShowSidebar}
        variant={variant}
      />
    </SidebarVariantContext.Provider>
  );
};

Sidebar.propTypes = {
  showSidebar: PropTypes.bool.isRequired,
  setShowSidebar: PropTypes.func.isRequired
};
export default Sidebar;
